
import { defineComponent, nextTick, ref } from "vue";
import { debounce } from "lodash";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import {
  IDanhMucToaXeResponseBase,
  IDonViResponseBase,
  IToaXeResponseBase,
} from "@/core/interfaces/ApiResponses";
import { usePagination } from "@/core/hooks/use-pagination";
import { getErrorMsg, swalNotification } from "@/core/helpers/utils";
import { useLoading } from "@/core/hooks/use-loading";
import { usePageHeader } from "@/core/hooks/usePageHeader";
import { TOA_XE } from "@/core/constants/permissions";
import useCapDonVi from "@/core/hooks/use-cap-don-vi";
import { DonViService } from "@/core/services/DonVi.service";
import ToaXeService from "@/core/services/ToaXe.service";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import DanhSachToaXeTable from "@/views/crafted/pages/toa-xe/danh-sach-toa-xe-table/DanhSachToaXeTable.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageHeaderFilters from "@/layout/header/PageHeaderFilters.vue";
import PageHeaderActions from "@/layout/header/PageHeaderActions.vue";
import PageHeaderActionItem from "@/layout/header/PageHeaderActionItem.vue";
import FilterItem from "@/layout/_shared/FilterItem.vue";
import DanhMucToaXeSearchInput from "@/components/shared/danh-muc-toa-xe-search-input/DanhMucToaXeSearchInput.vue";

export default defineComponent({
  name: "danh-sach-toa-xe",

  components: {
    PageContentBase,
    PageHeaderFilters,
    PageHeaderActions,
    PageHeaderActionItem,
    DanhSachToaXeTable,
    DanhMucToaXeSearchInput,
    Pagination,
    FilterItem,
  },

  setup() {
    const { push, goBack } = useRouterCustom();
    const { isCompanyLevel } = useCapDonVi();

    const {
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      loading,
    } = usePagination();

    const { startLoading, endLoading } = useLoading();
    const toaXeItems = ref<IToaXeResponseBase[]>([]);
    const dmToaXeId = ref<null | number>(null);
    const selectedDmToaXeLabel = ref("");
    const selectedDonViId = ref<number | "">("");

    document.title = "Toa xe - Đường sắt Hà Nội";

    const { searchQuery } = usePageHeader({
      title: "Danh sách toa xe",
      breadcrumbs: [{ text: "Toa xe" }],
      showSearch: true,
      searchPlaceholder: "Tìm theo mã hoặc tên",
      addLabel: "Thêm toa xe",
      addPermissions: [TOA_XE.TAO_MOI],
      onAdd: () => push("/quan-ly/toa-xe/tao-moi"),
    });

    return {
      push,
      goBack,
      toaXeItems,
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      loading,
      searchQuery,
      dmToaXeId,
      selectedDmToaXeLabel,
      selectedDonViId,
      isCompanyLevel,
      startLoading,
      endLoading,
      faPrint,
    };
  },

  data() {
    return {
      donViOptions: [] as IDonViResponseBase[],
    };
  },

  async mounted() {
    if (this.isCompanyLevel) {
      await this.fetchDonViOptions();
    }

    await this.fetchData();
  },

  watch: {
    async currentPage() {
      await this.fetchData();
    },

    async perPage() {
      if (this.currentPage === 1) {
        await this.fetchData();
        return;
      }
      this.currentPage = 1;
    },

    async dmToaXeId() {
      nextTick().then(async () => {
        if (this.currentPage === 1) {
          await this.fetchData();
          return;
        }
        this.currentPage = 1;
      });
    },

    async selectedDonViId() {
      this.currentPage = 1;
      await this.fetchData();
    },

    searchQuery: debounce(function () {
      nextTick().then(async () => {
        if (this.currentPage === 1) {
          await this.fetchData();
          return;
        }
        this.currentPage = 1;
      });
    }, 500),
  },

  methods: {
    openPrintViewer() {
      const tenDanhMuc =
        this.selectedDmToaXeLabel || (this.dmToaXeId ? "" : "Tất cả nhóm");

      const route = this.$router.resolve({
        name: "xem-danh-sach-toa-xe",
        params: { idDmToaXe: String(this.dmToaXeId || 0) },
        query: tenDanhMuc ? { ten: tenDanhMuc } : {},
      });

      window.open(route.href, "_blank");
    },

    handleDmClear() {
      this.dmToaXeId = null;
      this.selectedDmToaXeLabel = "";
    },

    handleDmSelect({ id, ten_danh_muc }: IDanhMucToaXeResponseBase) {
      this.dmToaXeId = id;
      this.selectedDmToaXeLabel = ten_danh_muc;
    },

    async fetchDonViOptions() {
      try {
        const {
          data: { data },
        } = await DonViService.fetchDepartmentsByCapDonVi(2);

        this.donViOptions = data;
      } catch (err) {
        await swalNotification(
          getErrorMsg(err, "Có lỗi khi tải danh sách đơn vị"),
          "error"
        );
      }
    },

    async fetchData() {
      try {
        this.startLoading();
        this.loading = true;

        const {
          data: {
            data: { data: res, total },
          },
        } = await ToaXeService.list(
          this.currentPage,
          this.perPage,
          this.dmToaXeId,
          this.searchQuery,
          this.selectedDonViId || undefined
        );

        this.toaXeItems = res;
        this.totalItems = total;
        this.loading = false;
        this.endLoading();
      } catch {
        this.endLoading();
        this.loading = false;

        await swalNotification(
          "Có lỗi xảy ra khi lấy danh sách toa xe",
          "error"
        );
      }
    },
  },
});
